import React, { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../firebase';

const AdminTeamMembers = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    description: '',
    image: '',
    order: 0,
    socialLinks: []
  });
  const [imageFile, setImageFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const getTeamMembersCallable = httpsCallable(functions, 'getTeamMembers');
      const result = await getTeamMembersCallable();
      setMembers(result.data.members || []);
    } catch (err) {
      console.error('Ekip üyeleri yüklenirken hata:', err);
      setError('Ekip üyeleri yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setEditingMember(null);
    setFormData({
      name: '',
      role: '',
      description: '',
      image: '',
      order: members.length,
      socialLinks: []
    });
    setImageFile(null);
    setShowModal(true);
  };

  const handleEdit = (member) => {
    setEditingMember(member);
    setFormData({
      name: member.name || '',
      role: member.role || '',
      description: member.description || '',
      image: member.image || '',
      order: member.order || 0,
      socialLinks: member.socialLinks || []
    });
    setImageFile(null);
    setShowModal(true);
  };

  const handleDelete = async (memberId) => {
    if (!window.confirm('Bu ekip üyesini silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      const deleteTeamMemberCallable = httpsCallable(functions, 'deleteTeamMember');
      await deleteTeamMemberCallable({ memberId });
      await fetchMembers();
      alert('Ekip üyesi silindi');
    } catch (err) {
      console.error('Silme hatası:', err);
      alert('Silme sırasında hata: ' + err.message);
    }
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setImageFile(e.target.files[0]);
    }
  };

  const uploadImage = async () => {
    if (!imageFile) return formData.image;

    setUploading(true);
    try {
      const timestamp = Date.now();
      const fileName = `team-members/${timestamp}_${imageFile.name}`;
      const storageRef = ref(storage, fileName);
      await uploadBytes(storageRef, imageFile);
      const downloadURL = await getDownloadURL(storageRef);
      return downloadURL;
    } catch (err) {
      console.error('Resim yükleme hatası:', err);
      throw new Error('Resim yüklenemedi');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      // Upload image if selected
      let imageUrl = formData.image;
      if (imageFile) {
        imageUrl = await uploadImage();
      }

      const memberData = {
        ...formData,
        image: imageUrl
      };

      if (editingMember) {
        // Update existing member
        const updateTeamMemberCallable = httpsCallable(functions, 'updateTeamMember');
        await updateTeamMemberCallable({
          memberId: editingMember.id,
          updates: memberData
        });
        alert('Ekip üyesi güncellendi');
      } else {
        // Create new member
        const createTeamMemberCallable = httpsCallable(functions, 'createTeamMember');
        await createTeamMemberCallable(memberData);
        alert('Yeni ekip üyesi eklendi');
      }

      setShowModal(false);
      await fetchMembers();
    } catch (err) {
      console.error('Kaydetme hatası:', err);
      setError('Kaydetme sırasında hata: ' + err.message);
    }
  };

  const handleSocialLinkChange = (index, field, value) => {
    const newLinks = [...formData.socialLinks];
    if (!newLinks[index]) {
      newLinks[index] = { label: '', url: '' };
    }
    newLinks[index][field] = value;
    setFormData({ ...formData, socialLinks: newLinks });
  };

  const addSocialLink = () => {
    setFormData({
      ...formData,
      socialLinks: [...formData.socialLinks, { label: '', url: '' }]
    });
  };

  const removeSocialLink = (index) => {
    const newLinks = formData.socialLinks.filter((_, i) => i !== index);
    setFormData({ ...formData, socialLinks: newLinks });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Ekip Üyeleri Yönetimi</h2>
        <button
          onClick={handleAddNew}
          className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
        >
          + Yeni Ekip Üyesi Ekle
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {members.map((member) => (
          <div key={member.id} className="bg-white rounded-lg shadow-md overflow-hidden">
            <img
              src={member.image || '/assets/images/default-avatar.png'}
              alt={member.name}
              className="w-full h-48 object-cover"
            />
            <div className="p-4">
              <h3 className="text-lg font-bold text-gray-900">{member.name}</h3>
              <p className="text-sm text-indigo-600 mb-2">{member.role}</p>
              <p className="text-sm text-gray-600 mb-3 line-clamp-3">{member.description}</p>
              
              {member.socialLinks && member.socialLinks.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs text-gray-500 mb-1">Sosyal Medya:</p>
                  <div className="flex flex-wrap gap-1">
                    {member.socialLinks.map((link, idx) => (
                      <span key={idx} className="text-xs bg-gray-100 px-2 py-1 rounded">
                        {link.label}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-between items-center pt-3 border-t">
                <span className="text-xs text-gray-500">Sıra: {member.order}</span>
                <div className="space-x-2">
                  <button
                    onClick={() => handleEdit(member)}
                    className="text-indigo-600 hover:text-indigo-800 text-sm"
                  >
                    Düzenle
                  </button>
                  <button
                    onClick={() => handleDelete(member.id)}
                    className="text-red-600 hover:text-red-800 text-sm"
                  >
                    Sil
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {members.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          Henüz ekip üyesi eklenmemiş. Yeni ekip üyesi eklemek için yukarıdaki butona tıklayın.
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-xl font-bold mb-4">
                {editingMember ? 'Ekip Üyesini Düzenle' : 'Yeni Ekip Üyesi Ekle'}
              </h3>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    İsim *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rol/Pozisyon *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Açıklama *
                  </label>
                  <textarea
                    required
                    rows={4}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Profil Fotoğrafı
                  </label>
                  {formData.image && !imageFile && (
                    <div className="mb-2">
                      <img src={formData.image} alt="Mevcut" className="h-20 w-20 object-cover rounded" />
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                  {imageFile && (
                    <p className="text-sm text-gray-600 mt-1">Seçilen: {imageFile.name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sıra (Listede gösterim sırası)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Sosyal Medya Linkleri
                    </label>
                    <button
                      type="button"
                      onClick={addSocialLink}
                      className="text-sm text-indigo-600 hover:text-indigo-800"
                    >
                      + Link Ekle
                    </button>
                  </div>
                  {formData.socialLinks.map((link, index) => (
                    <div key={index} className="flex gap-2 mb-2">
                      <input
                        type="text"
                        placeholder="Platform (örn: Instagram)"
                        value={link.label || ''}
                        onChange={(e) => handleSocialLinkChange(index, 'label', e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                      />
                      <input
                        type="url"
                        placeholder="URL"
                        value={link.url || ''}
                        onChange={(e) => handleSocialLinkChange(index, 'url', e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => removeSocialLink(index)}
                        className="px-3 py-2 text-red-600 hover:text-red-800"
                      >
                        Sil
                      </button>
                    </div>
                  ))}
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    disabled={uploading}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-400"
                  >
                    {uploading ? 'Yükleniyor...' : editingMember ? 'Güncelle' : 'Ekle'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTeamMembers;
